import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Stack,
  Grid,
  InputAdornment
} from '@mui/material'
import { 
  Science as ScienceIcon, 
  Save as SaveIcon,
  WaterDrop as WaterIcon,
  Thermostat as TempIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material'
import { toLocalDateTimeString, fromLocalDateTimeString, isoToLocalDateTimeString } from '../../utils/dateHelpers'

const MeasurementForm = ({ measurement, defaultValues, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    free_chlorine: defaultValues?.free_chlorine || '',
    combined_chlorine: defaultValues?.combined_chlorine || '',
    ph: defaultValues?.ph || '',
    total_alkalinity: defaultValues?.total_alkalinity || '',
    cya_stabilizer: defaultValues?.cya_stabilizer || '',
    calcium_hardness: defaultValues?.calcium_hardness || '',
    water_temperature: defaultValues?.water_temperature || '',
    notes: '',
    measured_at: toLocalDateTimeString(), // YYYY-MM-DDTHH:MM format for datetime-local input in local timezone
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (measurement) {
      // If editing an existing measurement, use its values
      setFormData({
        free_chlorine: measurement.free_chlorine || '',
        combined_chlorine: measurement.combined_chlorine || '',
        ph: measurement.ph || '',
        total_alkalinity: measurement.total_alkalinity || '',
        cya_stabilizer: measurement.cya_stabilizer || '',
        calcium_hardness: measurement.calcium_hardness || '',
        water_temperature: measurement.water_temperature || '',
        notes: measurement.notes || '',
        measured_at: measurement.measured_at ? 
          isoToLocalDateTimeString(measurement.measured_at) : 
          toLocalDateTimeString(),
      })
    } else if (defaultValues) {
      // If creating new measurement, use default values from previous measurement
      setFormData({
        free_chlorine: defaultValues.free_chlorine || '',
        combined_chlorine: defaultValues.combined_chlorine || '',
        ph: defaultValues.ph || '',
        total_alkalinity: defaultValues.total_alkalinity || '',
        cya_stabilizer: defaultValues.cya_stabilizer || '',
        calcium_hardness: defaultValues.calcium_hardness || '',
        water_temperature: defaultValues.water_temperature || '',
        notes: '',
        measured_at: toLocalDateTimeString(),
      })
    }
  }, [measurement, defaultValues])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? '' : 
        name === 'measured_at' ? value :
        name === 'notes' ? value :
        parseFloat(value) || value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      // Convert empty strings to null for numeric fields and handle datetime
      const cleanData = Object.entries(formData).reduce((acc, [key, value]) => {
        if (key === 'notes') {
          acc[key] = value
        } else if (key === 'measured_at') {
          acc[key] = fromLocalDateTimeString(value)
        } else {
          acc[key] = value === '' || value === null ? null : value
        }
        return acc
      }, {})

      const result = await onSave(cleanData)
      if (result.error) {
        setError(result.error.message)
      } else {
        onCancel()
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const parameters = [
    { 
      key: 'free_chlorine', 
      label: 'Free Chlorine', 
      unit: 'ppm', 
      step: '0.25', 
      min: '0', 
      max: '100',
      icon: <WaterIcon />,
      category: 'Chlorine'
    },
    { 
      key: 'combined_chlorine', 
      label: 'Combined Chlorine', 
      unit: 'ppm', 
      step: '0.25', 
      min: '0', 
      max: '100',
      icon: <WaterIcon />,
      category: 'Chlorine'
    },
    { 
      key: 'ph', 
      label: 'pH', 
      unit: '', 
      step: '0.2', 
      min: '6.0', 
      max: '20',
      icon: <ScienceIcon />,
      category: 'Balance'
    },
    { 
      key: 'total_alkalinity', 
      label: 'Total Alkalinity', 
      unit: 'ppm', 
      step: '1', 
      min: '0', 
      max: '10000',
      icon: <ScienceIcon />,
      category: 'Balance'
    },
    { 
      key: 'cya_stabilizer', 
      label: 'CYA/Stabilizer', 
      unit: 'ppm', 
      step: '1', 
      min: '0', 
      max: '1000',
      icon: <ScienceIcon />,
      category: 'Balance'
    },
    { 
      key: 'calcium_hardness', 
      label: 'Calcium Hardness', 
      unit: 'ppm', 
      step: '1', 
      min: '0', 
      max: '10000',
      icon: <ScienceIcon />,
      category: 'Balance'
    },
    { 
      key: 'water_temperature', 
      label: 'Water Temperature', 
      unit: '°F', 
      step: '1', 
      min: '32', 
      max: '120',
      icon: <TempIcon />,
      category: 'Environmental'
    },
  ]

  const groupedParameters = parameters.reduce((groups, param) => {
    if (!groups[param.category]) {
      groups[param.category] = []
    }
    groups[param.category].push(param)
    return groups
  }, {})

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 3, 
        borderRadius: 3,
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        maxWidth: 'lg',
        mx: 'auto'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <ScienceIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
          {measurement ? 'Edit Measurement' : 'New Measurement'}
        </Typography>
      </Box>
      
      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={4}>
          {error && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {/* Date/Time Section */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  fontWeight: 600,
                  color: 'primary.main',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'primary.light'
                }}
              >
                Measurement Date & Time
              </Typography>
            </Box>
            <TextField
              name="measured_at"
              label="Date & Time"
              type="datetime-local"
              value={formData.measured_at}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ScheduleIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                }
              }}
            />
          </Box>

          {Object.entries(groupedParameters).map(([category, params]) => (
            <Box key={category}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    px: 2,
                    py: 0.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'primary.light'
                  }}
                >
                  {category}
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {params.map(({ key, label, unit, step, min, max, icon }) => (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <TextField
                      name={key}
                      label={label}
                      placeholder={`Enter ${label.toLowerCase()}`}
                      type="number"
                      value={formData[key]}
                      onChange={handleChange}
                      fullWidth
                      variant="outlined"
                      inputProps={{
                        step: step,
                        min: min,
                        max: max
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {React.cloneElement(icon, { color: 'action', fontSize: 'small' })}
                          </InputAdornment>
                        ),
                        endAdornment: unit && (
                          <InputAdornment position="end">
                            <Typography variant="caption" color="text.secondary">
                              {unit}
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        }
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}

          <TextField
            name="notes"
            label="Notes (Optional)"
            placeholder="Any additional notes about this measurement..."
            value={formData.notes}
            onChange={handleChange}
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
              }
            }}
          />

          <Stack direction="row" spacing={2} sx={{ pt: 2 }}>
            <Button
              variant="outlined"
              onClick={onCancel}
              fullWidth
              size="large"
              sx={{ 
                borderRadius: 2,
                py: 1.5,
                textTransform: 'none',
                fontSize: '1.1rem'
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              fullWidth
              size="large"
              startIcon={loading ? null : <SaveIcon />}
              sx={{ 
                borderRadius: 2,
                py: 1.5,
                textTransform: 'none',
                fontSize: '1.1rem',
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
              }}
            >
              {loading ? 'Saving...' : 'Save Measurement'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  )
}

export default MeasurementForm